import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    Button,
    DataTable,
    FileInput,
    Heading,
    Layer,
    Meter,
    Page,
    PageContent,
    Pagination,
    Tag,
    Text,
    TextInput,
    Tip,
} from 'grommet';
import { Close } from 'grommet-icons';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';
import { API } from '../../api';
import JobService from '../../api/job-service';
import AppConstants from '../../helpers/AppConstants';
import { Utils } from '../../helpers/Utils';
import QueryString from 'qs';
import PropTypes from 'prop-types';
import { EventLog } from '../../components/EventLog';
import { Duplicate, Trash } from 'grommet-icons';
import { EditCandidacyModalButton } from './EditCandidacyModalButton';
import { JobManualReportButton } from './JobManualReportButton';
import { AddJobReportRecipientButton } from './AddJobReportRecipientButton';
import { MessageCandidatesButton } from './MessageCandidatesButton';
import { EditJobSeatsModal } from './EditJobSeatsModalButton';
import { GenerateMatchesButton } from './GenerateMatchesButton';

const EditJobButton = ({ jobId, navigate }) => (
    <Box align="center">
        <Button
            primary
            justify="center"
            size="large"
            label="Edit"
            onClick={() => navigate(`/jobs/${jobId}/edit`)}
        />
    </Box>
);

const DuplicateJobButton = ({ job, navigate }) => {
    const handleDuplicate = () => {
        const sanitizedJobAttributes = (({
            /* eslint-disable no-unused-vars */
            candidacies,
            createdAt,
            events,
            publishedAt,
            tasks,
            updatedAt,
            /* eslint-enable no-unused-vars */
            ...o
        }) => o)(job);
        const state = {
            duplicateJob: {
                ...job,
                ...sanitizedJobAttributes,
                locations: job.job_locations?.map((j) => j.location),
                title: `${job.title} - Copy`,
            },
        };
        navigate('/jobs/create', { state });
    };

    return (
        <Box>
            <Button
                primary
                size="large"
                label={
                    <Text size="small">
                        <Duplicate color="white" size="small" /> Duplicate
                    </Text>
                }
                onClick={handleDuplicate}
            />
        </Box>
    );
};

const CandidacyPieChart = ({ values, showVizTip, vizMessage }) => (
    <Box pad="small" align="center" basis="large" fill="horizontal">
        <Meter
            values={values}
            type="pie"
            thickness="large"
            margin={'none'}
            alignSelf="center"
        />
        {showVizTip && (
            <Box
                align="center"
                background="white"
                border={{
                    color: 'enrich-green-light',
                    size: '3px',
                }}
                gap="small"
                justify="center"
                pad="small"
                round="6px">
                {vizMessage}
            </Box>
        )}
    </Box>
);

const CandidacyBarChart = ({ values, total }) => (
    <Box pad="small" align="center" basis="large" fill="horizontal">
        <Box fill="horizontal" gap="small">
            <Meter
                values={values}
                type="bar"
                thickness="medium"
                max={total || 1}
            />
            <Box direction="row" wrap gap="small">
                {values.map((status) => (
                    <Box
                        key={status.label}
                        direction="row"
                        align="center"
                        gap="xsmall">
                        <Box
                            background={status.color}
                            width="12px"
                            height="12px"
                            round="2px"
                        />
                        <Text size="small">
                            {status.label.replace('_', ' ')} ({status.value})
                        </Text>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);

const CLIENT_JOB_STATUSES = ['open', 'preparing'];

const JobsSummaryActionsBar = ({
    job,
    navigate,
    showAdminActions = true,
    showMatchActions = showAdminActions,
    readOnly = false,
    onCloseJobClick = null,
    onUpdateJobToOpen = null,
    onUpdateJobToPreparing = null,
}) => (
    <Box
        direction="row-responsive"
        gap="medium"
        justify="end"
        fill="horizontal">
        {showAdminActions && !readOnly && (
            <EditJobButton jobId={job.id} navigate={navigate} />
        )}
        {!readOnly && <JobManualReportButton job={job} />}
        {showMatchActions && (
            <Box>
                <GenerateMatchesButton job={job} />
            </Box>
        )}
        {showAdminActions && !readOnly && (
            <DuplicateJobButton job={job} navigate={navigate} />
        )}
        {!showAdminActions &&
            onUpdateJobToOpen &&
            job.status === 'preparing' && (
            <Button
                primary
                size="large"
                label="Update Job to Open"
                onClick={onUpdateJobToOpen}
            />
        )}
        {!showAdminActions &&
            onUpdateJobToPreparing &&
            job.status === 'open' && (
            <Button
                secondary
                size="large"
                label="Update Job to Future Job"
                onClick={onUpdateJobToPreparing}
            />
        )}
        {!showAdminActions && onCloseJobClick && job.status !== 'closed' && (
            <Box>
                <Button
                    primary
                    size="large"
                    color="status-critical"
                    label="Close Job"
                    onClick={onCloseJobClick}
                />
            </Box>
        )}
    </Box>
);

JobsSummaryActionsBar.propTypes = {
    job: PropTypes.shape({
        id: PropTypes.number,
        status: PropTypes.string,
    }).isRequired,
    navigate: PropTypes.func.isRequired,
    showAdminActions: PropTypes.bool,
    showMatchActions: PropTypes.bool,
    readOnly: PropTypes.bool,
    onCloseJobClick: PropTypes.func,
    onUpdateJobToOpen: PropTypes.func,
    onUpdateJobToPreparing: PropTypes.func,
};

const RemoveReportRecipientButton = ({ jobId, recipient, recipients }) => {
    const [busy, setBusy] = useState(false);

    const removeRecipient = async () => {
        setBusy(true);
        try {
            await new JobService().updateReportRecipients(
                jobId,
                recipients.filter((email) => email !== recipient),
            );
            location.reload();
        } catch (error) {
            console.log(error);
            setBusy(false);
        }
    };

    return (
        <Button
            pad="none"
            color="enrich-red"
            size="small"
            plain
            busy={busy}
            disabled={busy}
            a11yTitle={`Remove ${recipient}`}
            icon={<Trash size="medium" color="red" />}
            onClick={removeRecipient}
        />
    );
};

RemoveReportRecipientButton.propTypes = {
    jobId: PropTypes.number.isRequired,
    recipient: PropTypes.string.isRequired,
    recipients: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const JobNotFound = () => (
    <Page kind="full" pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
        <PageContent>
            <Box pad="large" align="center">
                <Heading level="2">Job Not Found</Heading>
                <Text>
                    The requested job could not be found or is missing required
                    data.
                </Text>
            </Box>
        </PageContent>
    </Page>
);

const isValidUrl = (url) => {
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
};

// The guard lives here, before any state hooks, so a revalidation that drops
// the job cannot change the number of hooks the content renders.
export const JobSummary = (props) => {
    const loaderData = Utils.deepFlatten(useLoaderData());
    const job = loaderData.job;
    if (!job || !job.company) return <JobNotFound />;
    return <JobSummaryContent {...props} loaderData={loaderData} />;
};

const JobSummaryContent = ({
    loaderData,
    showAdminActions = true,
    showMatchActions = showAdminActions,
    readOnly = false,
    showCandidacyEdit,
    renderMessageButton = null,
    onCloseJob = null,
    onChangeJobStatus = null,
    onUpdateApplicationLink = null,
    onUpdateApplicationLinkAndOpen = null,
    showDocumentUpload,
    showCaseStudyUpload,
    candidacyStatusEnums: candidacyStatusEnumsProp,
}) => {
    // showAdminActions picks the staff layout; readOnly removes every edit
    // action from it.
    const canEdit = showAdminActions && !readOnly;
    const canEditCandidacy = showCandidacyEdit ?? canEdit;
    const navigate = useNavigate();
    const { companySlug } = useParams();
    const job = loaderData.job;
    const candidacyStatusEnums =
        candidacyStatusEnumsProp ?? loaderData.candidacyStatusEnums;
    const { job_missed_match_deadline } = AppConstants.eventTypes;
    const visibleJobEvents = job?.events?.filter(
        (event) => event.type !== job_missed_match_deadline,
    );

    const company = job.company;
    const pipeline = job.pipeline;

    const candidaciesWithContactData = job.candidacies
        .filter((candidacy) => candidacy.contact != null)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const candidacyTableStepOptions = [25, 50, 100];
    const reportRecipientTableStepOptions = [5, 10];
    const [candidacies, setCandidacies] = useState(candidaciesWithContactData);
    const [candidacyPage, setCandidacyPage] = useState(1);
    const [totalCandidacyItems, setTotalCandidacyItems] = useState(0);
    const [candidacyPageSize, setCandidacyPageSize] = useState(
        candidacyTableStepOptions[0],
    );
    const [showEditJobSeatsModal, setShowEditJobSeatsModal] = useState(false);

    const reportRecipientsData =
        job.reportRecipients?.map((r, i) => ({
            id: i + 1,
            email: r,
        })) || [];

    const [reportRecipients, setReportRecipients] =
        useState(reportRecipientsData);
    const [reportRecipientPage, setReportRecipientPage] = useState(1);
    const [totalReportRecipientItems, setTotalReportRecipientItems] =
        useState(0);
    const [reportRecipientPageSize, setReportRecipientPageSize] = useState(
        reportRecipientTableStepOptions[0],
    );

    const [showVizTip, setShowVizTip] = useState();
    const [vizMessage, setVizMessage] = useState('');
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [closingJob, setClosingJob] = useState(false);
    const [closeJobError, setCloseJobError] = useState('');
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkDraft, setLinkDraft] = useState('');
    const [linkSaveError, setLinkSaveError] = useState('');
    const [savingApplicationLink, setSavingApplicationLink] = useState(false);
    const [pendingOpenOnLinkSave, setPendingOpenOnLinkSave] = useState(false);

    const linkError =
        linkDraft && !isValidUrl(linkDraft)
            ? 'Please enter a valid URL (e.g. https://example.com/apply)'
            : '';

    const [documents, setDocuments] = useState(job.documents || []);
    const [documentFiles, setDocumentFiles] = useState([]);
    const [uploadingDocuments, setUploadingDocuments] = useState(false);

    const [caseStudies, setCaseStudies] = useState(job.caseStudies || []);
    const [caseStudyFiles, setCaseStudyFiles] = useState([]);
    const [uploadingCaseStudies, setUploadingCaseStudies] = useState(false);

    const handleDocumentUpload = async () => {
        if (!documentFiles.length) return;
        setUploadingDocuments(true);
        try {
            const uploadedIds = [];
            for (const file of documentFiles) {
                const response = await API.postForm(
                    '/api/upload',
                    { files: file },
                    { headers: { 'Content-Type': 'multipart/form-data' } },
                );
                uploadedIds.push(...response.data.map((f) => f.id));
            }
            const response = await API.post(`/api/jobs/${job.id}/documents`, {
                mediaIds: uploadedIds,
            });
            setDocuments(response.data.data);
            setDocumentFiles([]);
        } catch (error) {
            console.log(error);
        } finally {
            setUploadingDocuments(false);
        }
    };

    const handleCaseStudyUpload = async () => {
        if (!caseStudyFiles.length) return;
        setUploadingCaseStudies(true);
        try {
            const uploadedIds = [];
            for (const file of caseStudyFiles) {
                const response = await API.postForm(
                    '/api/upload',
                    { files: file },
                    { headers: { 'Content-Type': 'multipart/form-data' } },
                );
                uploadedIds.push(...response.data.map((f) => f.id));
            }
            const response = await API.post(
                `/api/jobs/${job.id}/case-studies`,
                { mediaIds: uploadedIds },
            );
            setCaseStudies(response.data.data);
            setCaseStudyFiles([]);
        } catch (error) {
            console.log(error);
        } finally {
            setUploadingCaseStudies(false);
        }
    };

    const handleCaseStudyDelete = async (mediaId) => {
        try {
            const response = await API.delete(
                `/api/jobs/${job.id}/case-studies/${mediaId}`,
            );
            setCaseStudies(response.data.data);
        } catch (error) {
            console.log(error);
        }
    };

    const handleDocumentDelete = async (mediaId) => {
        try {
            const response = await API.delete(
                `/api/jobs/${job.id}/documents/${mediaId}`,
            );
            setDocuments(response.data.data);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (!candidacyPage) return;

        const pagedCandidacies = candidaciesWithContactData.slice(
            candidacyPage * candidacyPageSize - candidacyPageSize,
            candidacyPage * candidacyPageSize,
        );

        setCandidacyPage(candidacyPage);
        setCandidacies(pagedCandidacies);
        setTotalCandidacyItems(candidaciesWithContactData.length);
    }, [candidacyPage, candidacyPageSize]);

    useEffect(() => {
        if (!job.isReportingActive || !reportRecipientPage) return;

        const pagedReportRecipients = reportRecipientsData.slice(
            reportRecipientPage * reportRecipientPageSize -
                reportRecipientPageSize,
            reportRecipientPage * reportRecipientPageSize,
        );

        setReportRecipientPage(reportRecipientPage);
        setReportRecipients(pagedReportRecipients);
        setTotalReportRecipientItems(reportRecipientsData.length);
    }, [reportRecipientPage, reportRecipientPageSize]);

    const reportRecipientDataColumns = [
        {
            header: 'Email',
            size: 'large',
            property: 'email',
            sortable: true,
        },
    ];
    if (!readOnly)
        reportRecipientDataColumns.push({
            header: 'Action',
            size: 'small',
            property: 'id',
            primary: true,
            render: (recipient) => (
                <RemoveReportRecipientButton
                    jobId={job.id}
                    recipient={recipient.email}
                    recipients={reportRecipientsData.map((x) => x.email)}
                />
            ),
            sortable: false,
        });

    const candidacyDataColumns = [
        {
            header: 'Name',
            property: 'contact.lastName',
            render: (candidacy) => {
                const name = `${candidacy.contact.firstName} ${candidacy.contact.lastName}`;
                if (readOnly) return <Text>{name}</Text>;
                return (
                    <Anchor
                        href={`${job.id}/candidacies/${candidacy.id}`}
                        label={name}
                    />
                );
            },
            sortable: true,
        },
        {
            header: 'Status',
            property: 'status',
            sortable: true,
        },
        {
            header: 'Location',
            property: 'job_locations',
            render: (candidacy) => {
                const jobLocations = candidacy.job_locations ?? [];
                if (!jobLocations.length) return '';
                return jobLocations
                    .map(
                        (j) =>
                            j.location?.name ??
                            `${j.officeName || ''} ${j.mergeLocation || ''}`.trim(),
                    )
                    .filter(Boolean)
                    .join(', ');
            },
            sortable: false,
        },
        {
            header: 'Last Updated',
            property: 'updatedAt',
            render: (candidacy) => {
                return new Date(candidacy.updatedAt).toLocaleDateString(
                    'en-us',
                    {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                    },
                );
            },
            sortable: true,
        },
        ...(canEditCandidacy
            ? [
                {
                    header: 'Action',
                    property: 'id',
                    sortable: false,
                    primary: true,
                    render: (candidacy) => {
                        return (
                            <EditCandidacyModalButton
                                candidacy={candidacy}
                                candidacyStatusEnums={candidacyStatusEnums}
                            />
                        );
                    },
                },
            ]
            : []),
    ];

    const jobStatuses = [
        { label: 'assigned', color: 'enrich-grey' },
        { label: 'interested', color: 'enrich-green' },
        { label: 'applied', color: 'enrich-blue' },
        { label: 'interview', color: 'enrich-dark-grey' },
        { label: 'application_rejected', color: 'orange' },
        { label: 'closed', color: 'maroon' },
    ];
    const candidacyDistributionValues = jobStatuses
        .map((status) => {
            const numberOfCandidacies = candidaciesWithContactData.filter(
                (candidacy) => candidacy.status == status.label,
            ).length;
            return {
                ...status,
                value: numberOfCandidacies,
                onHover: (isHovering) => {
                    setVizMessage(`${numberOfCandidacies} ${status.label}`);
                    setShowVizTip(isHovering);
                },
            };
        })
        .filter((status) => status.value);

    const handleReportRecipientPageChange = ({
        page,
        startIndex,
        endIndex,
    }) => {
        const newPageSize = endIndex - startIndex;

        if (reportRecipientPageSize != newPageSize) {
            setReportRecipientPageSize(newPageSize);
            setReportRecipientPage(1);
            return;
        }
        setReportRecipientPage(page);
    };

    const handleCandidacyPageChange = ({ page, startIndex, endIndex }) => {
        const newPageSize = endIndex - startIndex;
        if (candidacyPageSize != newPageSize) {
            setCandidacyPageSize(newPageSize);
            setCandidacyPage(1);
            return;
        }
        setCandidacyPage(page);
    };

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap="medium">
                <Box direction="row-responsive" align="start">
                    <Box width="large" direction="column">
                        <Heading level="2" margin="none">
                            {job.title}
                        </Heading>
                        {showAdminActions && (
                            <Heading level="3" margin="none">
                                <Anchor
                                    label={`${company.name}`}
                                    href={`/companies/${company.id}`}
                                />
                            </Heading>
                        )}
                    </Box>
                    <JobsSummaryActionsBar
                        job={job}
                        navigate={navigate}
                        showAdminActions={showAdminActions}
                        showMatchActions={showMatchActions}
                        readOnly={readOnly}
                        onCloseJobClick={
                            onCloseJob ? () => setShowCloseConfirm(true) : null
                        }
                        onUpdateJobToOpen={
                            onChangeJobStatus
                                ? () => {
                                    if (job.applicationLink) {
                                        onChangeJobStatus('open');
                                    } else {
                                        setLinkDraft('');
                                        setLinkSaveError('');
                                        setPendingOpenOnLinkSave(true);
                                        setShowLinkModal(true);
                                    }
                                }
                                : null
                        }
                        onUpdateJobToPreparing={
                            onChangeJobStatus
                                ? () => onChangeJobStatus('preparing')
                                : null
                        }
                    />
                </Box>

                <Box direction="row-responsive" gap={{ column: 'medium' }}>
                    {canEdit ? (
                        <Tip content="click to edit job seats">
                            <Tag
                                onClick={() => {
                                    setShowEditJobSeatsModal(true);
                                }}
                                background="enrich-blue"
                                name="Seats "
                                value={job.seats || 0}
                            />
                        </Tip>
                    ) : (
                        <Tag
                            background="enrich-blue"
                            name="Seats "
                            value={job.seats || 0}
                        />
                    )}

                    {showEditJobSeatsModal && (
                        <EditJobSeatsModal
                            job={job}
                            setShowEditJobSeatsModal={setShowEditJobSeatsModal}
                        />
                    )}

                    <Tag
                        background={{ color: 'enrich-blue' }}
                        name="Report Status "
                        value={job.isReportingActive ? 'Active' : 'Inactive'}
                    />
                    {showAdminActions && pipeline && readOnly && (
                        <Tag name="Pipeline" value={pipeline.name} />
                    )}
                    {canEdit && pipeline && (
                        <Tip content="click to go to pipeline">
                            <Tag
                                name="Pipeline"
                                value={pipeline.name}
                                onClick={() => {
                                    navigate(`/pipelines/${pipeline.id}`);
                                }}
                            />
                        </Tip>
                    )}
                    {(job.applicationLink || onUpdateApplicationLink) && (
                        <Box direction="row" align="center" gap="xsmall">
                            {job.applicationLink && (
                                <Anchor
                                    href={job.applicationLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    label={job.applicationLink}
                                />
                            )}
                            {!job.applicationLink &&
                                onUpdateApplicationLink && (
                                <Text size="small" color="dark-4">
                                        No application link set
                                </Text>
                            )}
                            {onUpdateApplicationLink && (
                                <Button
                                    plain
                                    size="small"
                                    aria-label="Edit application link"
                                    label="Edit"
                                    onClick={() => {
                                        setLinkDraft(job.applicationLink || '');
                                        setLinkSaveError('');
                                        setShowLinkModal(true);
                                    }}
                                />
                            )}
                        </Box>
                    )}
                </Box>

                {showCloseConfirm && (
                    <Layer
                        onClickOutside={() => {
                            if (closingJob) return;
                            setShowCloseConfirm(false);
                            setCloseJobError('');
                        }}
                        onEsc={() => {
                            if (closingJob) return;
                            setShowCloseConfirm(false);
                            setCloseJobError('');
                        }}
                        position="center">
                        <Box
                            background="white"
                            pad="medium"
                            round="6px"
                            border={{
                                color: 'enrich-green-light',
                                size: '3px',
                            }}
                            gap="medium"
                            width="medium">
                            <Box
                                direction="row"
                                justify="between"
                                align="center">
                                <Heading
                                    level="3"
                                    margin={{ vertical: 'none' }}>
                                    Close Job
                                </Heading>
                                <Close
                                    onClick={() => {
                                        if (closingJob) return;
                                        setShowCloseConfirm(false);
                                        setCloseJobError('');
                                    }}
                                    cursor="pointer"
                                />
                            </Box>
                            <Text>
                                Are you sure you want to close this job? This
                                action cannot be undone from the portal.
                            </Text>
                            {closeJobError && (
                                <Text size="small" color="status-critical">
                                    {closeJobError}
                                </Text>
                            )}
                            <Box direction="row" gap="small" justify="end">
                                <Button
                                    secondary
                                    label="Cancel"
                                    disabled={closingJob}
                                    onClick={() => {
                                        setShowCloseConfirm(false);
                                        setCloseJobError('');
                                    }}
                                />
                                <Button
                                    primary
                                    color="status-critical"
                                    label="Confirm"
                                    busy={closingJob}
                                    onClick={async () => {
                                        setClosingJob(true);
                                        setCloseJobError('');
                                        try {
                                            await onCloseJob();
                                            setShowCloseConfirm(false);
                                        } catch (error) {
                                            setCloseJobError(
                                                error?.message ||
                                                    'Something went wrong. Please try again.',
                                            );
                                        } finally {
                                            setClosingJob(false);
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    </Layer>
                )}

                {showLinkModal && (
                    <Layer
                        onClickOutside={() => {
                            setShowLinkModal(false);
                            setLinkSaveError('');
                            setPendingOpenOnLinkSave(false);
                        }}
                        onEsc={() => {
                            setShowLinkModal(false);
                            setLinkSaveError('');
                            setPendingOpenOnLinkSave(false);
                        }}
                        position="center">
                        <Box
                            background="white"
                            pad="medium"
                            round="6px"
                            border={{
                                color: 'enrich-green-light',
                                size: '3px',
                            }}
                            gap="medium"
                            width="medium">
                            <Box
                                direction="row"
                                justify="between"
                                align="center">
                                <Heading
                                    level="3"
                                    margin={{ vertical: 'none' }}>
                                    Application Link
                                </Heading>
                                <Close
                                    onClick={() => {
                                        setShowLinkModal(false);
                                        setLinkSaveError('');
                                        setPendingOpenOnLinkSave(false);
                                    }}
                                    cursor="pointer"
                                />
                            </Box>
                            {pendingOpenOnLinkSave && (
                                <Text size="small" color="status-warning">
                                    An application link is required before this
                                    job can be set to open.
                                </Text>
                            )}
                            <Box gap="xsmall">
                                <TextInput
                                    aria-label="Application link"
                                    placeholder="https://careers.example.com/apply"
                                    value={linkDraft}
                                    onChange={(e) => {
                                        setLinkDraft(e.target.value);
                                        setLinkSaveError('');
                                    }}
                                />
                                {linkError && (
                                    <Text size="small" color="status-critical">
                                        {linkError}
                                    </Text>
                                )}
                                {linkSaveError && (
                                    <Text size="small" color="status-critical">
                                        {linkSaveError}
                                    </Text>
                                )}
                            </Box>
                            <Box direction="row" gap="small" justify="end">
                                <Button
                                    secondary
                                    label="Cancel"
                                    onClick={() => {
                                        setShowLinkModal(false);
                                        setLinkSaveError('');
                                        setPendingOpenOnLinkSave(false);
                                    }}
                                />
                                <Button
                                    primary
                                    label={
                                        pendingOpenOnLinkSave
                                            ? 'Save & Open Job'
                                            : 'Save'
                                    }
                                    busy={savingApplicationLink}
                                    disabled={
                                        !linkDraft ||
                                        !!linkError ||
                                        savingApplicationLink
                                    }
                                    onClick={async () => {
                                        setSavingApplicationLink(true);
                                        setLinkSaveError('');
                                        try {
                                            if (pendingOpenOnLinkSave) {
                                                await onUpdateApplicationLinkAndOpen(
                                                    linkDraft,
                                                );
                                            } else {
                                                await onUpdateApplicationLink(
                                                    linkDraft,
                                                );
                                            }
                                            setShowLinkModal(false);
                                            setPendingOpenOnLinkSave(false);
                                        } catch (error) {
                                            setLinkSaveError(
                                                error?.message ||
                                                    'Something went wrong. Please try again.',
                                            );
                                        } finally {
                                            setSavingApplicationLink(false);
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    </Layer>
                )}

                <Box direction="row-responsive" fill="horizontal" gap="small">
                    {showAdminActions ? (
                        <CandidacyPieChart
                            values={candidacyDistributionValues}
                            showVizTip={showVizTip}
                            vizMessage={vizMessage}
                        />
                    ) : (
                        <CandidacyBarChart
                            values={candidacyDistributionValues}
                            total={candidaciesWithContactData.length}
                        />
                    )}

                    {job.isReportingActive == true && (
                        <Box basis="xlarge" fill="horizontal">
                            <Box
                                direction="row-responsive"
                                justify="between"
                                fill="horizontal"
                                pad={{ right: 'small' }}>
                                <Heading level="3">Report Recipients</Heading>
                                {!readOnly && (
                                    <Box justify="center">
                                        <AddJobReportRecipientButton
                                            jobId={job.id}
                                            // eslint-disable-next-line max-len
                                            reportRecipients={reportRecipientsData.map(
                                                (x) => x.email,
                                            )}
                                        />
                                    </Box>
                                )}
                            </Box>

                            <Box
                                direction="row-responsive"
                                elevation="large"
                                pad="medium"
                                fill="horizontal">
                                <DataTable
                                    sortable
                                    data={reportRecipients}
                                    columns={reportRecipientDataColumns}
                                    fill="horizontal"
                                />
                            </Box>

                            <Box
                                align="center"
                                justify="center"
                                margin={{ top: 'medium', bottom: 'none' }}>
                                <Pagination
                                    numberItems={totalReportRecipientItems}
                                    onChange={({
                                        page,
                                        startIndex,
                                        endIndex,
                                    }) => {
                                        handleReportRecipientPageChange({
                                            page,
                                            startIndex,
                                            endIndex,
                                        });
                                    }}
                                    size="small"
                                    step={reportRecipientPageSize}
                                    stepOptions={
                                        reportRecipientTableStepOptions
                                    }
                                    summary={true}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box>
                    <Box
                        direction="row-responsive"
                        justify="between"
                        pad={{ right: 'small' }}>
                        <Heading level="3">Candidacies</Heading>
                        <Box direction="row-responsive" gap="small">
                            {canEdit && (
                                <Box justify="center">
                                    <MessageCandidatesButton
                                        candidacyStatusEnums={
                                            candidacyStatusEnums
                                        }
                                        job={job}
                                    />
                                </Box>
                            )}
                            {!showAdminActions && renderMessageButton && (
                                <Box justify="center">
                                    {renderMessageButton(
                                        job,
                                        candidacyStatusEnums,
                                    )}
                                </Box>
                            )}
                            {!readOnly && (
                                <Box justify="center">
                                    <Button
                                        secondary
                                        size="large"
                                        label="View closed candidacies"
                                        onClick={() =>
                                            navigate(
                                                companySlug
                                                    ? `/${companySlug}/jobs/${job.id}/closed`
                                                    : `/jobs/${job.id}/closed`,
                                            )
                                        }
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Box elevation="large" pad="medium" fill="horizontal">
                        <DataTable
                            sortable
                            data={candidacies}
                            columns={candidacyDataColumns}
                        />
                    </Box>
                </Box>

                <Box align="center" justify="center">
                    <Pagination
                        numberItems={totalCandidacyItems}
                        onChange={({ page, startIndex, endIndex }) => {
                            handleCandidacyPageChange({
                                page,
                                startIndex,
                                endIndex,
                            });
                        }}
                        size="small"
                        step={candidacyPageSize}
                        stepOptions={candidacyTableStepOptions}
                        summary={true}
                    />
                </Box>

                <Box>
                    <Heading level="3">Recent Updates</Heading>
                    <Box
                        pad="medium"
                        elevation="large"
                        height={{ min: 'xsmall' }}
                        fill="horizontal">
                        <Box
                            fill="horizontal"
                            gap="xsmall"
                            overflow={{ vertical: 'auto', horizontal: 'auto' }}>
                            {visibleJobEvents?.length > 0 ? (
                                visibleJobEvents.map((event) => (
                                    <EventLog
                                        key={event.id}
                                        event={event}
                                        companySlug={companySlug}
                                    />
                                ))
                            ) : (
                                <Text>No Update</Text>
                            )}
                        </Box>
                    </Box>
                </Box>

                {(documents.length > 0 || showDocumentUpload) && (
                    <Box gap="small">
                        <Heading level="3">Documents</Heading>
                        <Box elevation="large" pad="medium" gap="small">
                            {documents.map((doc) => (
                                <Box
                                    key={doc.id}
                                    direction="row"
                                    justify="between"
                                    align="center">
                                    <Anchor
                                        href={doc.url}
                                        target="_blank"
                                        label={doc.name}
                                    />
                                    {showDocumentUpload && (
                                        <Button
                                            plain
                                            label={
                                                <Text
                                                    color="status-critical"
                                                    size="small">
                                                    Remove
                                                </Text>
                                            }
                                            onClick={() =>
                                                handleDocumentDelete(doc.id)
                                            }
                                        />
                                    )}
                                </Box>
                            ))}
                            {showDocumentUpload && (
                                <Box
                                    direction="row"
                                    gap="medium"
                                    align="end"
                                    margin={{ top: 'small' }}>
                                    <Box fill="horizontal">
                                        <FileInput
                                            multiple
                                            onChange={(event) =>
                                                setDocumentFiles(
                                                    Array.from(
                                                        event.target.files,
                                                    ),
                                                )
                                            }
                                        />
                                    </Box>
                                    <Button
                                        primary
                                        label="Upload"
                                        busy={uploadingDocuments}
                                        disabled={!documentFiles.length}
                                        onClick={handleDocumentUpload}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                {(caseStudies.length > 0 || showCaseStudyUpload) && (
                    <Box gap="small">
                        <Heading level="3">Case Studies</Heading>
                        <Box elevation="large" pad="medium" gap="small">
                            {caseStudies.map((file) => (
                                <Box
                                    key={file.id}
                                    direction="row"
                                    justify="between"
                                    align="center">
                                    <Anchor
                                        href={file.url}
                                        target="_blank"
                                        label={file.name}
                                    />
                                    {showCaseStudyUpload && (
                                        <Button
                                            plain
                                            label={
                                                <Text
                                                    color="status-critical"
                                                    size="small">
                                                    Remove
                                                </Text>
                                            }
                                            onClick={() =>
                                                handleCaseStudyDelete(file.id)
                                            }
                                        />
                                    )}
                                </Box>
                            ))}
                            {showCaseStudyUpload && (
                                <Box
                                    direction="row"
                                    gap="medium"
                                    align="end"
                                    margin={{ top: 'small' }}>
                                    <Box fill="horizontal">
                                        <FileInput
                                            multiple
                                            onChange={(event) =>
                                                setCaseStudyFiles(
                                                    Array.from(
                                                        event.target.files,
                                                    ),
                                                )
                                            }
                                        />
                                    </Box>
                                    <Button
                                        primary
                                        label="Upload"
                                        busy={uploadingCaseStudies}
                                        disabled={!caseStudyFiles.length}
                                        onClick={handleCaseStudyUpload}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </PageContent>
        </Page>
    );
};

JobSummary.propTypes = {
    showAdminActions: PropTypes.bool,
    showMatchActions: PropTypes.bool,
    readOnly: PropTypes.bool,
    showCandidacyEdit: PropTypes.bool,
    renderMessageButton: PropTypes.func,
    onCloseJob: PropTypes.func,
    onChangeJobStatus: PropTypes.func,
    onUpdateApplicationLink: PropTypes.func,
    onUpdateApplicationLinkAndOpen: PropTypes.func,
    showDocumentUpload: PropTypes.bool,
    showCaseStudyUpload: PropTypes.bool,
    candidacyStatusEnums: PropTypes.array,
};

JobSummaryContent.propTypes = {
    ...JobSummary.propTypes,
    loaderData: PropTypes.object.isRequired,
};

const getCandidacyStatusEnums = async () => {
    const query = `query myQuery {
        getFilterFields {
            candidacyStatuses {
                id
                name
            }
        }
    }`;

    try {
        const graphqlResponse = await API.post('graphql', { query });
        return (
            graphqlResponse.data.data?.getFilterFields?.candidacyStatuses || []
        );
    } catch (error) {
        console.log(error);
        return [];
    }
};

const getJob = async (jobId) => {
    const query = QueryString.stringify({
        populate: {
            candidacies: {
                populate: {
                    contact: true,
                    job_locations: {
                        populate: ['location'],
                    },
                },
                filters: {
                    status: {
                        $notIn: AppConstants.closedCandidacyStatuses,
                    },
                },
            },
            company: true,
            events: {
                populate: {
                    candidacy: {
                        populate: {
                            job_locations: {
                                populate: ['location'],
                            },
                        },
                    },
                    contact: true,
                    job: true,
                },
                sort: 'createdAt:desc',
                limit: 40,
            },
            idealMajors: true,
            industries: true,
            jobFunctions: true,
            job_locations: {
                populate: ['location'],
            },
            relevantTechnicalSkills: true,
            tasks: true,
            pipeline: {
                fields: ['id', 'name'],
            },
            documents: true,
            caseStudies: true,
        },
    });

    try {
        const response = await API.get(`/api/jobs/${jobId}?${query}`);
        return response.data.data;
    } catch (error) {
        console.log(error);
        return {};
    }
};

export async function loader({ params }) {
    const job = await getJob(params.jobId);
    const candidacyStatusEnums = await getCandidacyStatusEnums();
    return { job, candidacyStatusEnums };
}
